/**
 * Der Schreibweg EINER Position - gemeinsame Regel fuer `Drawer/MainTab/Positionen.tsx` und
 * `Drawer/CalcTab/EkTab.tsx#positionPatchen` (Task 12). Bis dahin stand dieselbe Ableitung
 * ZWEIMAL da: `Positionen.tsx` kannte seit Task 11 den Rueckfall auf `kLieferantenBestellungPos`
 * und die Sentinel-`rowVersion` `0`, `EkTab.tsx` verlangte weiterhin STRIKT `jtlPosId` UND eine
 * ganzzahlige `rowVersion` - in den 2132 Bestellungen ohne `order_item_extras`-Zeile liess sich der
 * EK-Reiter deshalb nicht speichern, obwohl der Positionsreiter es seit Task 11 laengst konnte. Die
 * Ableitung steht jetzt an GENAU einem Ort; beide Aufrufer benutzen sie unveraendert.
 *
 * ================= ZWEI QUELLEN FUER DIE POSITIONSKENNUNG =================
 *
 * `position.jtlPosId` kommt aus der Hub-Zeile (`order_item_extras`, gespreizt in
 * `jtlBestellungen.ts#alsOrderItem`) und fehlt deshalb GENAU dann, wenn es diese Zeile noch nicht
 * gibt. JTL liefert dieselbe Zahl aber IMMER mit, unter `kLieferantenBestellungPos`
 * (`alsOrderItem` setzt sie auf jeder Position, ob mit oder ohne Hub-Zeile) - und laut
 * `jtlBestellungen.ts#hubPositionenNachPosId` IST `jtl_pos_id` genau diese Spalte: "`jtl_pos_id`
 * IST die `kLieferantenBestellungPos`". Der Nachschlag bevorzugt deshalb `jtlPosId`, faellt aber auf
 * `kLieferantenBestellungPos` zurueck - bei einer bestehenden Hub-Zeile sind beide ohnehin gleich,
 * der Vorrang ist also folgenlos.
 *
 * ================= DIE SENTINEL `0` FUER EINE UNBEKANNTE `rowVersion` =================
 *
 * `rowVersion` MUSS die Route als Zahl mitbekommen (`typeof rowVersion !== "number"`,
 * `backend/src/http/routes/orders.ts`), GEPRUEFT wird sie dort aber nur, wenn die Zeile schon
 * existiert - beim Anlegen (`legeItemExtraAn`, `backend/src/domaene/items.ts`) liest niemand
 * `erwarteteVersion` (Kopfkommentar dort: "wird beim INSERT NICHT geprueft"). Fehlt `rowVersion`
 * hier (dieselbe Ursache wie bei `jtlPosId` oben), wird deshalb NICHTS erfunden, das wie eine echte
 * Version aussieht - stattdessen die Sentinel `0`, wortgleich zur selben Entscheidung bei
 * `App.tsx#drawerAendern` (`extrasRowVersion || 0`): jede Migration setzt
 * `order_item_extras.row_version integer NOT NULL DEFAULT 1` (`001_fundament.sql:158`), eine ECHTE
 * Zeile traegt also NIE `0` - der Wert kann folglich nie zufaellig auf die tatsaechliche Version
 * treffen. Existiert die Zeile ENTGEGEN dieser Annahme doch schon (ein Wettlauf: ein anderer Client
 * hat sie zwischen Laden und Klicken angelegt), antwortet der Server mit einem echten 409 samt
 * Serverstand - kein stilles Ueberschreiben, derselbe Fehlschlag wie bei jedem anderen
 * Versionskonflikt.
 *
 * Der Aufrufer prueft selbst, ob er ueberhaupt einen Rueckruf (`onPositionAendern`) hat - das ist
 * KEIN Teil dieser Funktion, weil beide Aufrufer die Pruefung an einer je eigenen Stelle brauchen
 * (`Positionen.tsx#schreibwegVon` auch fuer `flagSetzen`s Preis-Sonderfall, `EkTab.tsx` nur fuer
 * `positionPatchen`).
 *
 * ================= DIE SENTINEL GILT NUR FUER EINE FEHLENDE `rowVersion` (Task 14, Befund 3) =====
 *
 * Vor der Zusammenlegung (Task 12) verlangte `EkTab.tsx#positionPatchen` STRIKT
 * `Number.isInteger(rowVersion)` - diese Pruefung ist bei der Konsolidierung hierher verlorengegangen.
 * Ohne sie ginge eine VORHANDENE, aber nicht-ganzzahlige `rowVersion` (verunreinigte Daten - laut
 * Migration `NOT NULL DEFAULT 1`, sollte also nie vorkommen, ist aber keine Annahme wert) unveraendert
 * an den Server hinaus und schlaege dort erst mit einem 400 fehl (`typeof rowVersion !== "number"`
 * greift hier nicht, `Number.isInteger(1.5)` ist `false`, aber `1.5` bleibt eine `number`), statt schon
 * hier lokal auf den alten, lauten Weg zurueckzufallen. Die Sentinel `0` selbst bleibt UNVERAENDERT
 * fuer den Fall, dass `rowVersion` gaenzlich FEHLT (`undefined`/`null`) - nur DANN ist `0` die
 * richtige, weil garantiert nie zufaellig treffende Ersatzversion.
 */
import type { OrderItem } from "./types.ts";

/** Die Kennung, ueber die eine Position schreibbar ist, samt der Version, gegen die geschrieben wird. */
export interface PositionsSchreibweg {
  readonly jtlPosId: number;
  readonly rowVersion: number;
}

/**
 * Der Schreibweg der Position an Index `i` in `positionen` - `null`, wenn sie GAR KEINE
 * JTL-Positionskennung traegt (weder `jtlPosId` noch `kLieferantenBestellungPos`); dann bleibt nur
 * der alte, bestellungsweite Weg.
 */
export function schreibwegVon(
  positionen: readonly OrderItem[],
  i: number,
): PositionsSchreibweg | null {
  const position = positionen[i] as Record<string, unknown> | undefined;
  if (!position) return null;
  const jtlPosId = Number(position["jtlPosId"] ?? position["kLieferantenBestellungPos"]);
  if (!Number.isInteger(jtlPosId) || jtlPosId <= 0) return null;
  const rowVersionRoh = position["rowVersion"];
  const rowVersion = rowVersionRoh === undefined || rowVersionRoh === null ? 0 : Number(rowVersionRoh);
  if (!Number.isInteger(rowVersion)) return null;
  return { jtlPosId, rowVersion };
}
